/**
 * <license header>
 */

import RestClient from '../../integration/rest-client';
import CustomLogger from '../custom-logger';
import ImsToken from '../ims-token';

/**
 * RuntimeApiGatewayService for Adobe Runtime API Gateway
 *
 * This service provides a flexible way to interact with Adobe I/O Runtime API Gateway
 * endpoints with authentication and authorization support.
 *
 * Key Features:
 * - Automatic IMS token generation and management
 * - Built-in authentication headers
 * - Centralized error handling
 * - Support for common HTTP methods (GET, POST, PUT, DELETE)
 *
 * @example
 * ```typescript
 * const service = new RuntimeApiGatewayService(
 *   'client-id',
 *   'client-secret',
 *   'tech-account-id',
 *   'tech@example.com',
 *   'org-id',
 *   ['openid', 'AdobeID'],
 *   'my-namespace-12345'
 * );
 *
 * // GET request
 * const data = await service.get('v1/my-endpoint');
 *
 * // POST request
 * const result = await service.post('v1/my-endpoint', { key: 'value' });
 *
 * // PUT request
 * const updated = await service.put('v1/my-endpoint', { id: 1, name: 'updated' });
 *
 * // DELETE request
 * const deleted = await service.delete('v1/my-endpoint');
 * ```
 */
export class RuntimeApiGatewayService {
  /** Base URL for the Adobe I/O Runtime APIs */
  private static readonly BASE_URL = 'https://adobeioruntime.net/apis';

  /** The namespace identifier for the Adobe I/O Runtime actions */
  private readonly namespace: string;

  /** IMS organization ID */
  private readonly imsOrgId: string;

  /** ImsToken instance for authentication */
  private readonly imsToken: ImsToken;

  /** RestClient instance for making HTTP requests */
  private readonly restClient: RestClient;

  /** CustomLogger instance for logging */
  private readonly customLogger: CustomLogger;

  /**
   * Creates an instance of RuntimeApiGatewayService
   *
   * @param clientId - OAuth client ID for Adobe IMS authentication
   * @param clientSecret - OAuth client secret for Adobe IMS authentication
   * @param technicalAccountId - Technical account ID for service-to-service authentication
   * @param technicalAccountEmail - Technical account email for service-to-service authentication
   * @param imsOrgId - IMS organization ID
   * @param scopes - Array of scopes required for the token
   * @param namespace - The Adobe I/O Runtime namespace identifier
   * @param logger - Optional logger instance for logging operations
   * @example
   * ```typescript
   * const service = new RuntimeApiGatewayService(
   *   'client-id',
   *   'client-secret',
   *   'tech-account-id',
   *   'tech@example.com',
   *   'org-id',
   *   ['openid', 'AdobeID'],
   *   'my-namespace-12345',
   *   logger // optional
   * );
   * ```
   */
  constructor(
    clientId: string,
    clientSecret: string,
    technicalAccountId: string,
    technicalAccountEmail: string,
    imsOrgId: string,
    scopes: Array<string>,
    namespace: string,
    logger: any = null
  ) {
    this.namespace = namespace;
    this.imsOrgId = imsOrgId;
    this.customLogger = new CustomLogger(logger);
    this.imsToken = new ImsToken(
      clientId,
      clientSecret,
      technicalAccountId,
      technicalAccountEmail,
      imsOrgId,
      scopes,
      logger,
      'runtime_api_gateway_token',
      'runtime-api-gateway-context'
    );
    this.restClient = new RestClient();
  }

  /**
   * Builds the complete API endpoint URL
   *
   * @param endpoint - API endpoint path (e.g., 'v1/my-endpoint')
   * @returns The fully constructed endpoint URL
   * @private
   */
  private buildEndpoint(endpoint: string): string {
    return `${RuntimeApiGatewayService.BASE_URL}/${this.namespace}/${endpoint}`;
  }

  /**
   * Gets the authenticated headers for API requests
   *
   * @returns A promise that resolves with the headers object including authentication
   * @throws {Error} If token generation fails
   * @private
   */
  private async getAuthenticatedHeaders(): Promise<Record<string, string>> {
    this.customLogger.debug('Generating authenticated headers for API request');
    const token = await this.imsToken.execute();

    if (!token) {
      this.customLogger.error('Failed to generate IMS token for authenticated headers');
      throw new Error('Failed to generate IMS token');
    }

    this.customLogger.debug('Successfully generated authenticated headers');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-gw-ims-org-id': this.imsOrgId,
    };
  }

  /**
   * Performs a GET request to the Runtime API Gateway
   *
   * @param endpoint - API endpoint path (e.g., 'v1/my-endpoint')
   * @param additionalHeaders - Optional additional headers to include in the request
   * @returns A promise that resolves with the API response data
   * @throws {Error} If the API request fails
   * @example
   * ```typescript
   * const service = new RuntimeApiGatewayService(...);
   * const data = await service.get('v1/my-endpoint');
   *
   * // With additional headers
   * const data = await service.get('v1/my-endpoint', { 'Custom-Header': 'value' });
   * ```
   */
  async get(endpoint: string, additionalHeaders: Record<string, string> = {}): Promise<any> {
    try {
      const url = this.buildEndpoint(endpoint);
      this.customLogger.info(`Performing GET request to: ${url}`);
      const headers = { ...(await this.getAuthenticatedHeaders()), ...additionalHeaders };
      this.customLogger.debug(`GET headers: ${JSON.stringify(headers)}`);

      const response = await this.restClient.get(url, headers, false);
      this.customLogger.debug(`GET response: ${JSON.stringify(response)}`);
      this.customLogger.info('GET request completed successfully');
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.customLogger.error(`GET request failed: ${errorMessage}`);
      throw new Error(`Runtime API gateway GET request failed: ${errorMessage}`);
    }
  }

  /**
   * Performs a POST request to the Runtime API Gateway
   *
   * @param endpoint - API endpoint path (e.g., 'v1/my-endpoint')
   * @param payload - The data to send in the request body
   * @param additionalHeaders - Optional additional headers to include in the request
   * @returns A promise that resolves with the API response data
   * @throws {Error} If the API request fails
   * @example
   * ```typescript
   * const service = new RuntimeApiGatewayService(...);
   * const result = await service.post('v1/my-endpoint', { key: 'value' });
   *
   * // With additional headers
   * const result = await service.post('v1/my-endpoint', { key: 'value' }, { 'Custom-Header': 'value' });
   * ```
   */
  async post(
    endpoint: string,
    payload: any,
    additionalHeaders: Record<string, string> = {}
  ): Promise<any> {
    try {
      const url = this.buildEndpoint(endpoint);
      this.customLogger.info(`Performing POST request to: ${url}`);
      this.customLogger.debug(`POST payload: ${JSON.stringify(payload)}`);
      const headers = { ...(await this.getAuthenticatedHeaders()), ...additionalHeaders };
      this.customLogger.debug(`POST headers: ${JSON.stringify(headers)}`);

      const response = await this.restClient.post(url, headers, payload, false);
      this.customLogger.debug(`POST response: ${JSON.stringify(response)}`);
      this.customLogger.info('POST request completed successfully');
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.customLogger.error(`POST request failed: ${errorMessage}`);
      throw new Error(`Runtime API gateway POST request failed: ${errorMessage}`);
    }
  }

  /**
   * Performs a PUT request to the Runtime API Gateway
   *
   * @param endpoint - API endpoint path (e.g., 'v1/my-endpoint')
   * @param payload - The data to send in the request body
   * @param additionalHeaders - Optional additional headers to include in the request
   * @returns A promise that resolves with the API response data
   * @throws {Error} If the API request fails
   * @example
   * ```typescript
   * const service = new RuntimeApiGatewayService(...);
   * const updated = await service.put('v1/my-endpoint', { id: 1, name: 'updated' });
   *
   * // With additional headers
   * const updated = await service.put('v1/my-endpoint', { id: 1 }, { 'Custom-Header': 'value' });
   * ```
   */
  async put(
    endpoint: string,
    payload: any,
    additionalHeaders: Record<string, string> = {}
  ): Promise<any> {
    try {
      const url = this.buildEndpoint(endpoint);
      this.customLogger.info(`Performing PUT request to: ${url}`);
      this.customLogger.debug(`PUT payload: ${JSON.stringify(payload)}`);
      const headers = { ...(await this.getAuthenticatedHeaders()), ...additionalHeaders };
      this.customLogger.debug(`PUT headers: ${JSON.stringify(headers)}`);

      const response = await this.restClient.put(url, headers, payload, false);
      this.customLogger.debug(`PUT response: ${JSON.stringify(response)}`);
      this.customLogger.info('PUT request completed successfully');
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.customLogger.error(`PUT request failed: ${errorMessage}`);
      throw new Error(`Runtime API gateway PUT request failed: ${errorMessage}`);
    }
  }

  /**
   * Performs a DELETE request to the Runtime API Gateway
   *
   * @param endpoint - API endpoint path (e.g., 'v1/my-endpoint')
   * @param additionalHeaders - Optional additional headers to include in the request
   * @returns A promise that resolves with the API response data
   * @throws {Error} If the API request fails
   * @example
   * ```typescript
   * const service = new RuntimeApiGatewayService(...);
   * const deleted = await service.delete('v1/my-endpoint');
   *
   * // With additional headers
   * const deleted = await service.delete('v1/my-endpoint', { 'Custom-Header': 'value' });
   * ```
   */
  async delete(endpoint: string, additionalHeaders: Record<string, string> = {}): Promise<any> {
    try {
      const url = this.buildEndpoint(endpoint);
      this.customLogger.info(`Performing DELETE request to: ${url}`);
      const headers = { ...(await this.getAuthenticatedHeaders()), ...additionalHeaders };
      this.customLogger.debug(`DELETE headers: ${JSON.stringify(headers)}`);

      const response = await this.restClient.delete(url, headers, false);
      this.customLogger.debug(`DELETE response: ${JSON.stringify(response)}`);
      this.customLogger.info('DELETE request completed successfully');
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.customLogger.error(`DELETE request failed: ${errorMessage}`);
      throw new Error(`Runtime API gateway DELETE request failed: ${errorMessage}`);
    }
  }
}
